import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Paper,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LoadingAnimation from "../../Animations/LoadingAnimation";
import FormatTime from "../../AdsPage/FormatTime";
import { fonts, headingFonts, noOutline } from "../../../Styles/Styles";
import OfferStatusModal from "./OfferStatusModal";

const ReceivedOfferSection = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
  });

  const fetchOffers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/offers/received-offers`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch offers");
      const data = await response.json();
      setOffers(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (response, successMessage, status) => {
    if (response.ok) {
      setStatusModal({
        open: true,
        type: "success",
        message: successMessage,
      });
      setError(null);
      fetchOffers();
    } else {
      response.json().then((data) => {
        setStatusModal({
          open: true,
          type: "error",
          message: data.message || `Failed to ${status} offer.`,
        });
      });
    }
  };

  const handleOfferStatusUpdate = async (offerId, status) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/offers/${status}/${offerId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      handleResponse(response, `Offer ${status}ed successfully!`, status);
      setOffers((prevOffers) =>
        prevOffers.map((offer) =>
          offer._id === offerId ? { ...offer, status } : offer
        )
      );
    } catch (error) {
      setStatusModal({
        open: true,
        type: "error",
        message: `Failed to ${status} offer. Please try again.`,
      });
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  if (loading) return <LoadingAnimation />;
  if (!offers.length)
    return <Typography sx={{ ...fonts }}>No offers received.</Typography>;

  return (
    <Box>
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headingFonts }}>Ad Title</TableCell>
              <TableCell sx={{ ...headingFonts }}>Amount</TableCell>
              <TableCell sx={{ ...headingFonts }}>Offered By</TableCell>
              <TableCell sx={{ ...headingFonts }}>Message</TableCell>
              <TableCell sx={{ ...headingFonts }}>Status</TableCell>
              <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => (
              <TableRow key={offer._id}>
                <TableCell sx={{ ...headingFonts }}>
                  <Link
                    to={`/ad/${offer.adId}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {offer.title}
                  </Link>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ ...fonts }}
                  >
                    <FormatTime createdAt={offer.createdAt} />
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...fonts }}>${offer.amount}</TableCell>
                <TableCell sx={{ ...fonts }}>{offer.firstName}</TableCell>
                <TableCell sx={{ ...fonts }}>{offer.offerMessage}</TableCell>
                <TableCell
                  sx={{
                    ...fonts,
                    color:
                      offer.status === "accepted"
                        ? "green"
                        : offer.status === "rejected"
                        ? "red"
                        : "gray",
                  }}
                >
                  {offer.status}
                </TableCell>
                <TableCell>
                  {offer.status === "pending" && (
                    <>
                      <Tooltip title="Accept Offer">
                        <IconButton
                          color="success"
                          onClick={() =>
                            handleOfferStatusUpdate(offer._id, "accept")
                          }
                          sx={{ ...noOutline }}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject Offer">
                        <IconButton
                          color="error"
                          onClick={() =>
                            handleOfferStatusUpdate(offer._id, "reject")
                          }
                          sx={{ ...noOutline }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <OfferStatusModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        setStatusModal={setStatusModal}
      />
    </Box>
  );
};

export default ReceivedOfferSection;
